import type { TFunction } from 'i18next'
import React from 'react'
import { Trans } from 'react-i18next'
import { FaEye, FaPen } from 'react-icons/fa'
import styled from 'styled-components'
import { formatEurosWithOpts } from '@root/utils'
import { humanizeLucaToolName } from '../builder/lucaToolPickerUtils'
import type {
  FilterCondition,
  SummarizeNodeConfigCatalogs,
  SummarizeNodeConfigOptions
} from './types'
import { summarizeAuditTrailCommentMessage } from './auditTrailCommentConfigUtils'
import { buildFilterSentence } from './filterSentenceUtils'
import { buildScheduledTriggerSummary } from './scheduledTriggerSentenceUtils'

const SummaryLead = styled.span`
  color: ${({ theme }) => theme.colors.metalGray};
  font-weight: 600;
`

const SummaryRest = styled.span`
  color: ${({ theme }) => theme.colors.iconGray};
`

const renderFilterSentence = (sentence: { lead: string; rest: string }): React.ReactNode => (
  <>
    <SummaryLead>{sentence.lead}</SummaryLead>
    {', '}
    <SummaryRest>{sentence.rest}</SummaryRest>
  </>
)

const readFilterConfig = (config: Record<string, unknown>) => ({
  filter_type: typeof config.filter_type === 'string' ? config.filter_type : '',
  match: config.match === 'any' ? ('any' as const) : ('all' as const),
  conditions: (Array.isArray(config.conditions) ? config.conditions : []).filter(
    (condition): condition is FilterCondition => typeof condition === 'object' && condition !== null
  )
})

export const summarizeFilterConfig = (
  config: Record<string, unknown>,
  t: TFunction,
  catalogs: SummarizeNodeConfigCatalogs
): React.ReactNode => {
  const parsed = readFilterConfig(config)
  if (parsed.conditions.length === 0) return null

  const sentence = buildFilterSentence(t, parsed, catalogs.filterCatalog, {
    accountLabels: catalogs.accountLabels,
    contactLabels: catalogs.contactLabels
  })
  return sentence ? renderFilterSentence(sentence) : null
}

export const summarizeSelectorConfig = (
  config: Record<string, unknown>,
  t: TFunction,
  catalogs: SummarizeNodeConfigCatalogs
): React.ReactNode => {
  const parsed = readFilterConfig(config)
  if (parsed.conditions.length === 0) {
    return t('workflows.nodes.selector.emptySummary')
  }

  return summarizeFilterConfig(config, t, catalogs)
}

export const summarizeEventTriggerConfig = (
  config: Record<string, unknown>,
  _t: TFunction,
  catalogs: SummarizeNodeConfigCatalogs
): React.ReactNode => {
  const eventType = typeof config.event_type === 'string' ? config.event_type : ''
  return catalogs.eventTriggerTypes.find(option => option.value === eventType)?.label ?? ''
}

export const summarizeScheduledTriggerConfig = (
  config: Record<string, unknown>,
  t: TFunction
): React.ReactNode => {
  const frequency = typeof config.frequency === 'string' ? config.frequency : 'daily'
  const hour = typeof config.hour === 'number' ? config.hour : 0
  const minute = typeof config.minute === 'number' ? config.minute : 0
  const dayOfWeek = typeof config.day_of_week === 'number' ? config.day_of_week : 1
  const dayOfMonth = typeof config.day_of_month === 'number' ? config.day_of_month : 1

  return buildScheduledTriggerSummary(t, {
    frequency,
    hour,
    minute,
    day_of_week: dayOfWeek,
    day_of_month: dayOfMonth
  })
}

export const summarizeWaitConfig = (
  config: Record<string, unknown>,
  t: TFunction
): React.ReactNode => {
  const amount = typeof config.amount === 'number' ? config.amount : 1
  const unit = typeof config.unit === 'string' ? config.unit : 'hours'
  const unitKey =
    unit === 'minutes'
      ? 'workflows.config.wait.units.minutes'
      : unit === 'days'
      ? 'workflows.config.wait.units.days'
      : 'workflows.config.wait.units.hours'

  return t('workflows.nodes.wait.summary', {
    amount,
    unit: t(unitKey, { count: amount })
  })
}

export const summarizeLucaConfig = (
  config: Record<string, unknown>,
  t: TFunction,
  catalogs: SummarizeNodeConfigCatalogs,
  options: SummarizeNodeConfigOptions = {}
): React.ReactNode => {
  const instructions = typeof config.instructions === 'string' ? config.instructions.trim() : ''
  const approvedTools = Array.isArray(config.approved_tools)
    ? config.approved_tools.filter(
        (name): name is string => typeof name === 'string' && name !== ''
      )
    : []
  const expanded = Boolean(options.expanded)
  const snippet =
    instructions && !expanded && instructions.length > 120
      ? `${instructions.slice(0, 117)}…`
      : instructions
  const visibleTools = approvedTools.slice(0, 3)
  const remainingToolCount = approvedTools.length - visibleTools.length

  return (
    <LucaSummary>
      {snippet && <LucaPrompt $expanded={expanded}>{snippet}</LucaPrompt>}
      <LucaToolSummary $hasPrompt={Boolean(snippet)}>
        {approvedTools.length === 0 ? (
          <LucaSummaryLine>
            <FaEye />
            <span>{t('workflows.nodes.luca.readOnly')}</span>
          </LucaSummaryLine>
        ) : (
          <>
            {visibleTools.map(name => (
              <LucaSummaryLine key={name}>
                <FaPen />
                <span>{catalogs.lucaToolTitles?.[name] || humanizeLucaToolName(name)}</span>
              </LucaSummaryLine>
            ))}
            {remainingToolCount > 0 && (
              <LucaSummaryMore>
                {t('workflows.nodes.luca.moreTools', { count: remainingToolCount })}
              </LucaSummaryMore>
            )}
          </>
        )}
      </LucaToolSummary>
    </LucaSummary>
  )
}

const LucaSummary = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`

const LucaPrompt = styled.div<{ $expanded: boolean }>`
  ${({ $expanded }) =>
    $expanded
      ? `white-space: pre-wrap;`
      : `
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
      `}
`

const LucaToolSummary = styled.div<{ $hasPrompt: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xxxs}rem;
  margin-top: ${({ theme, $hasPrompt }) => ($hasPrompt ? theme.spacing.xs : 0)}rem;
  padding-top: ${({ theme, $hasPrompt }) => ($hasPrompt ? theme.spacing.xs : 0)}rem;
  border-top: ${({ theme, $hasPrompt }) =>
    $hasPrompt ? `1px solid ${theme.colors.neutralGray}` : 'none'};
`

const LucaSummaryLine = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  min-width: 0;
  color: ${({ theme }) => theme.colors.metalGray};

  svg {
    flex-shrink: 0;
    width: ${({ theme }) => theme.fontSize.px10}rem;
    height: ${({ theme }) => theme.fontSize.px10}rem;
    color: ${({ theme }) => theme.colors.iconGray};
  }

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`

const LucaSummaryMore = styled.span`
  padding-left: ${({ theme }) => theme.spacing.md}rem;
  color: ${({ theme }) => theme.colors.iconGray};
  font-weight: 600;
`

export const summarizeAuditTrailCommentConfig = (
  config: Record<string, unknown>
): React.ReactNode => {
  const message = typeof config.message === 'string' ? config.message : ''
  const summary = summarizeAuditTrailCommentMessage(message)
  return summary || null
}

export const summarizeEmailSendConfig = (
  config: Record<string, unknown>,
  t: TFunction,
  catalogs: SummarizeNodeConfigCatalogs,
  options: SummarizeNodeConfigOptions = {}
): React.ReactNode => {
  const recipients = Array.isArray(config.recipients)
    ? config.recipients.filter((value): value is string => typeof value === 'string')
    : []
  const tags = Array.isArray(config.recipient_tags)
    ? config.recipient_tags.filter((value): value is string => typeof value === 'string')
    : []
  const [first, ...rest] = recipients
  const extra = Math.max(tags.length - 1, 0) + recipients.length
  const roleLabel = catalogs.usershipTags?.find(role => role.id === tags[0])?.plural ?? tags[0]
  const recipientSummary = tags.length ? (
    extra === 0 ? (
      <Trans
        i18nKey="workflows.nodes.emailSend.summaryToRole"
        values={{ role: roleLabel }}
        components={{ role: <SummaryLead /> }}
      />
    ) : (
      <Trans
        i18nKey="workflows.nodes.emailSend.summaryToRoleMore"
        count={extra}
        values={{ role: roleLabel, count: extra }}
        components={{ role: <SummaryLead /> }}
      />
    )
  ) : !first ? (
    t('workflows.nodes.emailSend.summaryNoRecipients')
  ) : rest.length === 0 ? (
    t('workflows.nodes.emailSend.summaryTo', { first })
  ) : (
    t('workflows.nodes.emailSend.summaryToMore', { first, count: rest.length })
  )
  const messageSummary =
    config.message_source === 'custom'
      ? t('workflows.nodes.emailSend.summaryCustom')
      : options.upstreamLabel
      ? t('workflows.nodes.emailSend.summaryFromUpstream', {
          step: options.upstreamLabel
        })
      : t('workflows.config.emailSend.messageFromFlow')
  return (
    <>
      {recipientSummary}
      {' · '}
      {messageSummary}
    </>
  )
}

export const summarizeOpaqueConfig = (config: Record<string, unknown>): React.ReactNode => {
  const values = Object.values(config || {})
    .map(value => String(value ?? '').trim())
    .filter(Boolean)
  return values.length > 0 ? values.join(' · ') : null
}

export const summarizeCreateDocumentConfig = (
  config: Record<string, unknown>,
  t: TFunction
): React.ReactNode => {
  const parts: string[] = [t('workflows.nodes.createDocument.summaryPerTransaction')]
  if (config.as_draft === true) {
    parts.push(t('workflows.nodes.createDocument.summaryDraft'))
  }

  const date = config.date
  if (date && typeof date === 'object' && 'source' in date) {
    const source = (date as { source?: string; rule?: string; days?: number; months?: number })
      .source
    const rule = (date as { rule?: string }).rule
    if (source === 'calc' && rule) {
      const months = (date as { months?: number }).months ?? 0
      const shortKey =
        rule === 'plusDays'
          ? t('workflows.nodes.createDocument.summaryPlusDays', {
              days: (date as { days?: number }).days ?? 0
            })
          : rule === 'plusMonthsLastDay'
          ? t('workflows.nodes.createDocument.summaryPlusMonthsLastDay', { months })
          : rule === 'plusMonthsFirstDay'
          ? t('workflows.nodes.createDocument.summaryPlusMonthsFirstDay', { months })
          : t(`workflows.nodes.createDocument.summaryDate.${rule}`)
      parts.push(t('workflows.nodes.createDocument.summaryDated', { rule: shortKey }))
    }
  }

  const amount = typeof config.amount === 'number' ? config.amount : null
  if (amount != null && amount !== 0) {
    parts.push(
      t('workflows.nodes.createDocument.summaryAmount', {
        amount: formatEurosWithOpts(amount)
      })
    )
  }

  return parts.join(' · ')
}
